package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.toSize
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupProperties
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.navigationevent.NavigationEventInfo
import androidx.navigationevent.compose.NavigationBackHandler
import androidx.navigationevent.compose.rememberNavigationEventState
import com.daytimegaming.ourdailyfinances.data.network.service.GeoApifyProperties
import com.daytimegaming.ourdailyfinances.presentation.preview.AddRealEstateHoldingPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.defaultColors
import com.daytimegaming.ourdailyfinances.presentation.util.immutableColors
import org.koin.compose.viewmodel.koinViewModel
import kotlin.random.Random

@Composable
fun AddRealEstateHoldingScreen(
    onNavigateBack: () -> Unit,
    viewModel: AddRealEstateHoldingViewModel = koinViewModel(key = remember { Random.nextInt().toString() }),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(state.isSuccess) {
        if (state.isSuccess) {
            onNavigateBack()
        }
    }

    AddRealEstateHoldingScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onNicknameChange = { viewModel.onNicknameChange(it) },
        onAddressLine1Change = { viewModel.onAddressLine1Change(it) },
        onAddressLine2Change = { viewModel.onAddressLine2Change(it) },
        onCityChange = { viewModel.onCityChange(it) },
        onStateChange = { viewModel.onStateChange(it) },
        onZipCodeChange = { viewModel.onZipCodeChange(it) },
        onCountyChange = { viewModel.onCountyChange(it) },
        onPurchasePriceChange = { viewModel.onPurchasePriceChange(it) },
        onMortgageAmountChange = { viewModel.onMortgageAmountChange(it) },
        onMortgageInterestRateChange = { viewModel.onMortgageInterestRateChange(it) },
        onAddHoldingClick = { viewModel.addHolding() },
        onSuggestionSelect = { viewModel.onSuggestionSelect(it) }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddRealEstateHoldingScreenContent(
    state: AddRealEstateHoldingState,
    onNavigateBack: () -> Unit,
    onNicknameChange: (String) -> Unit,
    onAddressLine1Change: (String) -> Unit,
    onAddressLine2Change: (String) -> Unit,
    onCityChange: (String) -> Unit,
    onStateChange: (String) -> Unit,
    onZipCodeChange: (String) -> Unit,
    onCountyChange: (String) -> Unit,
    onPurchasePriceChange: (String) -> Unit,
    onMortgageAmountChange: (String) -> Unit,
    onMortgageInterestRateChange: (String) -> Unit,
    onAddHoldingClick: () -> Unit,
    onSuggestionSelect: (GeoApifyProperties) -> Unit,
) {
    var showDiscardConfirmationDialog by remember { mutableStateOf(false) }
    var textFieldSize by remember { mutableStateOf(androidx.compose.ui.geometry.Size.Zero) }
    val density = LocalDensity.current

    var purchasePriceTextFieldValue by remember {
        mutableStateOf(TextFieldValue(text = state.purchasePrice, selection = TextRange(state.purchasePrice.length)))
    }
    LaunchedEffect(state.purchasePrice) {
        if (state.purchasePrice != purchasePriceTextFieldValue.text) {
            val diff = state.purchasePrice.length - purchasePriceTextFieldValue.text.length
            val newSelectionIndex = (purchasePriceTextFieldValue.selection.end + diff).coerceIn(0, state.purchasePrice.length)
            purchasePriceTextFieldValue = TextFieldValue(
                text = state.purchasePrice,
                selection = TextRange(newSelectionIndex)
            )
        }
    }
    var mortgageAmountTextFieldValue by remember {
        mutableStateOf(TextFieldValue(text = state.mortgageAmount, selection = TextRange(state.mortgageAmount.length)))
    }
    LaunchedEffect(state.mortgageAmount) {
        if (state.mortgageAmount != mortgageAmountTextFieldValue.text) {
            val diff = state.mortgageAmount.length - mortgageAmountTextFieldValue.text.length
            val newSelectionIndex = (mortgageAmountTextFieldValue.selection.end + diff).coerceIn(0, state.mortgageAmount.length)
            mortgageAmountTextFieldValue = TextFieldValue(
                text = state.mortgageAmount,
                selection = TextRange(newSelectionIndex)
            )
        }
    }

    var interestRateTextFieldValue by remember {
        mutableStateOf(TextFieldValue(text = state.mortgageInterestRate, selection = TextRange(state.mortgageInterestRate.length)))
    }
    LaunchedEffect(state.mortgageInterestRate) {
        if (state.mortgageInterestRate != interestRateTextFieldValue.text) {
            val diff = state.mortgageInterestRate.length - interestRateTextFieldValue.text.length
            val newSelectionIndex = (interestRateTextFieldValue.selection.end + diff).coerceIn(0, state.mortgageInterestRate.length)
            interestRateTextFieldValue = TextFieldValue(
                text = state.mortgageInterestRate,
                selection = TextRange(newSelectionIndex)
            )
        }
    }

    val navState = rememberNavigationEventState(NavigationEventInfo.None)

    NavigationBackHandler(
        state = navState,
        isBackEnabled = state.hasAnyInput,
        onBackCompleted = {
            showDiscardConfirmationDialog = true
        }
    )

    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "Add Real Estate Holding",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                navigationIcon = {
                    IconButton(
                        onClick = {
                            if (state.hasAnyInput) {
                                showDiscardConfirmationDialog = true
                            } else {
                                onNavigateBack()
                            }
                        }
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back",
                            tint = MaterialTheme.colorScheme.onSurface
                        )
                    }
                }
            )
        },
        bottomBar = {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Bottom))
                    .padding(horizontal = 16.dp, vertical = 8.dp)
            ) {
                if (state.isLoading) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                    }
                } else {
                    Button(
                        onClick = onAddHoldingClick,
                        enabled = state.isSaveEnabled,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = MaterialTheme.colorScheme.primary,
                            contentColor = Color(0xFF003731),
                            disabledContainerColor = MaterialTheme.colorScheme.primary.copy(alpha = 0.5f),
                            disabledContentColor = Color(0xFF003731).copy(alpha = 0.5f)
                        ),
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(56.dp)
                    ) {
                        Text(
                            text = "Add Holding",
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.titleMedium
                        )
                    }
                }
            }
        }
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            if (state.error != null) {
                Text(
                    text = state.error,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodyMedium,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
            }

            // Group 1: General Info
            GlassCard(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "General Info",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    OutlinedTextField(
                        value = state.nickname,
                        onValueChange = onNicknameChange,
                        label = { Text("Nickname (e.g. My Home, Mountain Cabin)") },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words)
                    )
                }
            }

            // Group 2: Address details
            GlassCard(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "Address Details",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    Box(modifier = Modifier.fillMaxWidth()) {
                        OutlinedTextField(
                            value = state.addressLine1,
                            onValueChange = onAddressLine1Change,
                            label = { Text("Address Line 1") },
                            singleLine = true,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier
                                .fillMaxWidth()
                                .onGloballyPositioned { coordinates ->
                                    textFieldSize = coordinates.size.toSize()
                                },
                            colors = OutlinedTextFieldDefaults.defaultColors(),
                            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words)
                        )
                        if (state.isAutocompleteLoading || state.suggestions.isNotEmpty()) {
                            Popup(
                                alignment = Alignment.TopStart,
                                offset = IntOffset(x = 0, y = textFieldSize.height.toInt()),
                                properties = PopupProperties(focusable = false)
                            ) {
                                Card(
                                    modifier = Modifier
                                        .width(with(density) { textFieldSize.width.toDp() })
                                        .heightIn(max = 240.dp)
                                        .padding(top = 4.dp),
                                    shape = RoundedCornerShape(8.dp),
                                    colors = CardDefaults.cardColors(
                                        containerColor = MaterialTheme.colorScheme.surfaceContainerLowest
                                    ),
                                    elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
                                ) {
                                    Column(
                                        modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState())
                                    ) {
                                        if (state.isAutocompleteLoading) {
                                            Row(
                                                verticalAlignment = Alignment.CenterVertically,
                                                horizontalArrangement = Arrangement.spacedBy(12.dp),
                                                modifier = Modifier.fillMaxWidth().padding(16.dp)
                                            ) {
                                                CircularProgressIndicator(
                                                    modifier = Modifier.size(18.dp),
                                                    strokeWidth = 2.dp,
                                                    color = MaterialTheme.colorScheme.primary
                                                )
                                                Text(
                                                    text = "Searching...",
                                                    style = MaterialTheme.typography.bodyMedium,
                                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                                )
                                            }
                                        } else {
                                            state.suggestions.forEach { suggestion ->
                                                TextButton(
                                                    onClick = { onSuggestionSelect(suggestion) },
                                                    modifier = Modifier.fillMaxWidth(),
                                                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
                                                    shape = RoundedCornerShape(0.dp),
                                                    colors = ButtonDefaults.textButtonColors(
                                                        contentColor = MaterialTheme.colorScheme.onSurface
                                                    )
                                                ) {
                                                    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.CenterStart) {
                                                        Text(
                                                            text = suggestion.formatted ?: "",
                                                            style = MaterialTheme.typography.bodyMedium,
                                                            maxLines = 2
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                    OutlinedTextField(
                        value = state.addressLine2,
                        onValueChange = onAddressLine2Change,
                        label = { Text("Address Line 2 (Optional)") },
                        singleLine = true,
                        enabled = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.immutableColors(),
                        keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words)
                    )
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        OutlinedTextField(
                            value = state.city,
                            onValueChange = onCityChange,
                            label = { Text("City") },
                            singleLine = true,
                            enabled = false,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.weight(1f),
                            colors = OutlinedTextFieldDefaults.immutableColors(),
                            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words)
                        )
                        OutlinedTextField(
                            value = state.state,
                            onValueChange = onStateChange,
                            label = { Text("State") },
                            singleLine = true,
                            enabled = false,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.weight(1f),
                            colors = OutlinedTextFieldDefaults.immutableColors(),
                            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Characters)
                        )
                    }
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        OutlinedTextField(
                            value = state.zipCode,
                            onValueChange = onZipCodeChange,
                            label = { Text("Zip Code") },
                            singleLine = true,
                            enabled = false,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.weight(1f),
                            colors = OutlinedTextFieldDefaults.immutableColors(),
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                        )
                        OutlinedTextField(
                            value = state.county,
                            onValueChange = onCountyChange,
                            label = { Text("County (Optional)") },
                            singleLine = true,
                            enabled = false,
                            shape = RoundedCornerShape(8.dp),
                            modifier = Modifier.weight(1f),
                            colors = OutlinedTextFieldDefaults.immutableColors(),
                            keyboardOptions = KeyboardOptions(capitalization = KeyboardCapitalization.Words)
                        )
                    }
                }
            }

            // Group 3: Financial details
            GlassCard(modifier = Modifier.fillMaxWidth()) {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text(
                        text = "Financial Details",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.primary
                    )
                    OutlinedTextField(
                        value = purchasePriceTextFieldValue,
                        onValueChange = { newValue ->
                            purchasePriceTextFieldValue = newValue
                            onPurchasePriceChange(newValue.text)
                        },
                        label = { Text("Purchase Price") },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                    )
                    OutlinedTextField(
                        value = mortgageAmountTextFieldValue,
                        onValueChange = { newValue ->
                            mortgageAmountTextFieldValue = newValue
                            onMortgageAmountChange(newValue.text)
                        },
                        label = { Text("Mortgage Amount (Optional)") },
                        supportingText = {
                            Text(
                                text = "If your mortgage can be brought in through any of your financial institutions, do not include a value here as it will cause an improper calculation on the net worth screens.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                    )
                    OutlinedTextField(
                        value = interestRateTextFieldValue,
                        onValueChange = { newValue ->
                            interestRateTextFieldValue = newValue
                            onMortgageInterestRateChange(newValue.text)
                        },
                        label = { Text("Mortgage Interest Rate (Optional %)") },
                        singleLine = true,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.fillMaxWidth(),
                        colors = OutlinedTextFieldDefaults.defaultColors(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal)
                    )
                }
            }
        }
    }

    if (showDiscardConfirmationDialog) {
        AlertDialog(
            onDismissRequest = { showDiscardConfirmationDialog = false },
            title = { Text("Discard Changes") },
            text = { Text("You have unsaved changes. Are you sure you want to discard them?") },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDiscardConfirmationDialog = false
                        onNavigateBack()
                    }
                ) {
                    Text("Discard", color = MaterialTheme.colorScheme.error)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { showDiscardConfirmationDialog = false }
                ) {
                    Text("Cancel")
                }
            }
        )
    }
}

@PreviewLightDark
@Composable
fun AddRealEstateHoldingScreenPreview(
    @PreviewParameter(AddRealEstateHoldingPreviewParameterProvider::class) state: AddRealEstateHoldingState
) {
    AppTheme(darkTheme = isSystemInDarkTheme(), dynamicColor = false) {
        Surface {
            AddRealEstateHoldingScreenContent(
                state = state,
                onNavigateBack = {},
                onNicknameChange = {},
                onAddressLine1Change = {},
                onAddressLine2Change = {},
                onCityChange = {},
                onStateChange = {},
                onZipCodeChange = {},
                onCountyChange = {},
                onPurchasePriceChange = {},
                onMortgageAmountChange = {},
                onMortgageInterestRateChange = {},
                onAddHoldingClick = {},
                onSuggestionSelect = {}
            )
        }
    }
}
